"use client";

import { useState, useEffect, useRef } from "react";
import { useRouter } from "next/navigation";
import { Eye, EyeOff, X } from "lucide-react";

// ─── Set this to your backend's base URL ─────────────────────────────────────
const API_BASE = process.env.NEXT_PUBLIC_API_URL || "";

export default function LoginPage() {
  const router = useRouter();

  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    remember: false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [mounted, setMounted] = useState(false);

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const ripplesRef = useRef<{ x: number; y: number; t: number }[]>([]);
  const animRef = useRef<number | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const oauthError = params.get("error");
    if (oauthError) {
      const messages: Record<string, string> = {
        google_failed   : "Google sign-in failed. Please try again.",
        facebook_failed : "Facebook sign-in failed. Please try again.",
        account_blocked : "Your account has been blocked.",
        account_inactive: "Your account is inactive.",
      };
      setError(messages[oauthError] || "Authentication failed. Please try again.");
    }
  }, []);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const DURATION = 1800;
    const MAX_R = 160;

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };

    resize();

    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    const draw = (now: number) => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      ripplesRef.current = ripplesRef.current.filter((r) => now - r.t < DURATION);

      for (const r of ripplesRef.current) {
        const p = (now - r.t) / DURATION;
        const eased = 1 - Math.pow(1 - p, 3);

        ctx.beginPath();
        ctx.arc(r.x, r.y, Math.max(0, eased * MAX_R), 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(199,171,95,${(1 - p) * 0.6})`;
        ctx.lineWidth = 1.5 * (1 - p) + 0.4;
        ctx.stroke();

        if (p > 0.15) {
          const p2 = (p - 0.15) / 0.85;
          ctx.beginPath();
          ctx.arc(r.x, r.y, Math.max(0, p2 * MAX_R * 0.55), 0, Math.PI * 2);
          ctx.strokeStyle = `rgba(199,171,95,${(1 - p2) * 0.28})`;
          ctx.lineWidth = (1 - p2) + 0.25;
          ctx.stroke();
        }
      }

      animRef.current = requestAnimationFrame(draw);
    };

    animRef.current = requestAnimationFrame(draw);

    return () => {
      if (animRef.current) cancelAnimationFrame(animRef.current);
      ro.disconnect();
    };
  }, []);

  const spawnRipple = (cx: number, cy: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    ripplesRef.current.push({
      x: cx - rect.left,
      y: cy - rect.top,
      t: performance.now(),
    });
  };

  // ── Email / password login ────────────────────────────────────────────────
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isLoading) return;

    setError("");
    setIsLoading(true);

    try {
      const response = await fetch(`${API_BASE}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: formData.email.trim().toLowerCase(),
          password: formData.password,
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        setError(data.message || "Invalid email or password.");
        return;
      }

      if (!data?.token) {
        setError("Login failed. Token not received.");
        return;
      }

      if (formData.remember) {
        sessionStorage.removeItem("auth_token");
        sessionStorage.removeItem("auth_user");
        localStorage.setItem("auth_token", data.token);
        localStorage.setItem("auth_user", JSON.stringify(data.user));
      } else {
        localStorage.removeItem("auth_token");
        localStorage.removeItem("auth_user");
        sessionStorage.setItem("auth_token", data.token);
        sessionStorage.setItem("auth_user", JSON.stringify(data.user));
      }

      const role = String(data.user?.role || "").toLowerCase().trim();
      if (["admin", "editor", "master", "tailor", "sales"].includes(role)) {
        router.replace("/admin/dashboard");
      } else {
        router.replace("/");
      }
    } catch (err) {
      console.error("Login error:", err);
      setError("Network error. Please check your connection and try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // ── OAuth redirect helpers ────────────────────────────────────────────────
  const handleGoogleLogin = () => {
    window.location.href = `${API_BASE}/api/auth/google`;
  };

  // ── Close / go home ──────────────────────────────────────────────────────
  const handleClose = () => {
    router.replace("/");
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;1,300;1,400&family=DM+Mono:wght@300;400&display=swap');

        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

        html, body { height: 100%; }
        @media (min-width: 769px) {
          html, body { overflow: hidden; }
        }

        header, footer, nav { display: none !important; }

        :root {
          --navy: #051173;
          --gold: #C7AB5F;
          --white: #ffffff;
          --black: #000000;
          --error: #c0392b;
        }

        .lr {
          font-family: 'Cormorant Garamond', Georgia, serif;
          position: fixed;
          inset: 0;
          width: 100vw;
          height: 100vh;
          height: 100dvh;
          overflow: hidden;
          z-index: 9999;
          cursor: crosshair;
        }

        @media (max-width: 768px) {
          .lr {
            position: relative;
            min-height: 100dvh;
            overflow: visible;
          }
        }

        .bg-video {
          position: fixed;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
          z-index: 0;
        }

        .bg-scrim {
          position: fixed;
          inset: 0;
          background: linear-gradient(
            135deg,
            rgba(0,0,0,0.38) 0%,
            rgba(0,0,0,0.18) 50%,
            rgba(0,0,0,0.52) 100%
          );
          z-index: 1;
        }

        .bg-tex {
          position: fixed;
          inset: 0;
          background: repeating-linear-gradient(
            -55deg,
            transparent,
            transparent 90px,
            rgba(199,171,95,0.025) 90px,
            rgba(199,171,95,0.025) 91px
          );
          z-index: 2;
        }

        .bg-canvas {
          position: fixed;
          inset: 0;
          width: 100%;
          height: 100%;
          pointer-events: none;
          z-index: 3;
        }

        .bg-compass {
          position: fixed;
          top: 2.5rem;
          left: 2.5rem;
          z-index: 4;
          opacity: 0.22;
          pointer-events: none;
        }

        .c-tl, .c-br, .c-tr, .c-bl {
          position: fixed;
          width: 52px;
          height: 52px;
          z-index: 4;
          pointer-events: none;
        }

        .c-tl { top: 20px; left: 20px; border-top: 1.5px solid rgba(199,171,95,0.55); border-left: 1.5px solid rgba(199,171,95,0.55); }
        .c-tr { top: 20px; right: 20px; border-top: 1.5px solid rgba(199,171,95,0.55); border-right: 1.5px solid rgba(199,171,95,0.55); }
        .c-bl { bottom: 20px; left: 20px; border-bottom: 1.5px solid rgba(199,171,95,0.55); border-left: 1.5px solid rgba(199,171,95,0.55); }
        .c-br { bottom: 20px; right: 20px; border-bottom: 1.5px solid rgba(199,171,95,0.55); border-right: 1.5px solid rgba(199,171,95,0.55); }

        .bg-brand {
          position: fixed;
          bottom: 3rem;
          left: 3rem;
          z-index: 4;
          pointer-events: none;
        }

        .bg-brand-name {
          font-size: clamp(2rem, 3.5vw, 3.5rem);
          font-style: italic;
          font-weight: 300;
          color: rgba(255,255,255,0.18);
          line-height: 0.85;
          letter-spacing: -0.02em;
        }

        .bg-brand-name span { color: rgba(199,171,95,0.28); display: block; }

        .bg-brand-tag {
          font-family: 'DM Mono', monospace;
          font-size: 9px;
          letter-spacing: 0.28em;
          text-transform: uppercase;
          color: rgba(255,255,255,0.18);
          margin-top: 0.6rem;
        }

        .bg-stats {
          position: fixed;
          bottom: 3rem;
          right: 3rem;
          z-index: 4;
          pointer-events: none;
          display: flex;
          flex-direction: row;
          gap: 0.75rem;
        }

        .bg-stat {
          border: 1px solid rgba(199,171,95,0.2);
          background: rgba(0,0,0,0.25);
          backdrop-filter: blur(6px);
          border-radius: 6px;
          padding: 0.45rem 0.8rem;
          display: flex;
          flex-direction: column;
          align-items: flex-start;
        }

        .bg-stat-n { font-family: 'Cormorant Garamond', serif; font-size: 1.2rem; font-style: italic; color: var(--gold); line-height: 1; }
        .bg-stat-l { font-family: 'DM Mono', monospace; font-size: 8px; letter-spacing: 0.2em; text-transform: uppercase; color: rgba(255,255,255,0.3); margin-top: 2px; }

        .bg-badge {
          position: fixed;
          top: 2.4rem;
          right: 2.5rem;
          z-index: 4;
          pointer-events: none;
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          border: 1px solid rgba(199,171,95,0.32);
          background: rgba(0,0,0,0.28);
          backdrop-filter: blur(8px);
          border-radius: 100px;
          padding: 0.3rem 0.8rem;
          font-family: 'DM Mono', monospace;
          font-size: 9px;
          letter-spacing: 0.28em;
          text-transform: uppercase;
          color: var(--gold);
        }

        .bg-badge::before {
          content: '';
          width: 5px;
          height: 5px;
          border-radius: 50%;
          background: var(--gold);
          flex-shrink: 0;
        }

        .fc-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: clamp(1rem, 2.5vw, 1.5rem);
        }

        .fc-header .gold-bar {
          width: 2.5rem;
          height: 2px;
          background: var(--gold);
          flex-shrink: 0;
        }

        .close-btn {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          border: 1.5px solid rgba(5,17,115,0.2);
          background: rgba(5,17,115,0.05);
          color: #051173;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: background 0.22s, border-color 0.22s, color 0.22s, transform 0.15s;
          -webkit-tap-highlight-color: transparent;
          flex-shrink: 0;
        }

        .close-btn:hover {
          background: #051173;
          border-color: #051173;
          color: #fff;
          transform: scale(1.08);
        }

        .close-btn:active { transform: scale(0.95); }

        .form-wrap {
          position: relative;
          z-index: 5;
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 100dvh;
          padding: clamp(0.75rem, 3vw, 2rem) clamp(0.75rem, 3vw, 1.5rem);
          pointer-events: none;
        }

        .fc {
          width: 100%;
          max-width: 420px;
          background: rgba(255,255,255,0.96);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border-radius: 16px;
          padding: clamp(1.5rem, 4vw, 2.75rem) clamp(1.25rem, 4vw, 2.5rem) clamp(1.25rem, 3vw, 2.25rem);
          box-shadow: 0 24px 64px rgba(0,0,0,0.28), 0 0 0 1px rgba(199,171,95,0.15);
          pointer-events: all;
          opacity: 0;
          transform: translateY(28px) scale(0.98);
          transition: opacity 0.7s ease, transform 0.7s ease;
        }

        @media (min-width: 769px) {
          .fc {
            max-height: calc(100dvh - 4rem);
            overflow-y: auto;
            scrollbar-width: none;
            -ms-overflow-style: none;
          }
          .fc::-webkit-scrollbar { display: none; }
        }

        .fc.on { opacity: 1; transform: translateY(0) scale(1); }

        .f-eye { font-family: 'DM Mono', monospace; font-size: 9px; letter-spacing: 0.32em; text-transform: uppercase; color: #051173; margin-bottom: 0.5rem; }
        .f-title { font-size: clamp(1.9rem, 5vw, 2.6rem); font-weight: 300; font-style: italic; color: var(--black); line-height: 0.9; letter-spacing: -0.02em; margin-bottom: 0.4rem; }
        .f-sub { font-family: 'DM Mono', monospace; font-size: 9px; letter-spacing: 0.14em; text-transform: uppercase; color: #051173; margin-bottom: clamp(1.25rem, 3vw, 1.75rem); }

        .alert-error {
          border-radius: 8px;
          padding: 0.75rem 1rem;
          margin-bottom: 1.1rem;
          background: rgba(192,57,43,0.07);
          border: 1px solid rgba(192,57,43,0.25);
          color: var(--error);
          font-family: 'DM Mono', monospace;
          font-size: 9.5px;
          letter-spacing: 0.08em;
          display: flex;
          align-items: flex-start;
          gap: 0.5rem;
          animation: fadeSlideDown 0.3s ease both;
        }

        @keyframes fadeSlideDown {
          from { opacity: 0; transform: translateY(-8px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .alert-icon { flex-shrink: 0; font-size: 12px; margin-top: 1px; }

        .fg { margin-bottom: clamp(1rem, 2.5vw, 1.5rem); position: relative; }
        .fl { font-family: 'DM Mono', monospace; font-size: 9px; letter-spacing: 0.25em; text-transform: uppercase; color: var(--navy); margin-bottom: 0.4rem; display: block; }
        .fiw { position: relative; }

        .fi {
          width: 100%;
          border: none;
          border-bottom: 1.5px solid #ddd;
          background: transparent;
          padding: 0.6rem 2.5rem 0.6rem 0;
          font-family: 'Cormorant Garamond', Georgia, serif;
          font-size: clamp(15px, 3.5vw, 17px);
          color: var(--black);
          outline: none;
          transition: border-color 0.25s;
          letter-spacing: 0.02em;
          -webkit-appearance: none;
          border-radius: 0;
        }

        .fi::placeholder { color: #bbb; font-style: italic; }
        .fi:focus { border-color: var(--navy); }
        .fi.hv { border-color: var(--gold); }

        .fu {
          position: absolute;
          bottom: 0;
          left: 0;
          width: 0;
          height: 2px;
          background: linear-gradient(90deg, var(--navy), var(--gold));
          transition: width 0.35s ease;
        }

        .fi:focus ~ .fu { width: 100%; }

        .eye-btn {
          position: absolute;
          right: 0;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          cursor: pointer;
          color: #051173;
          padding: 0.25rem;
          transition: color 0.2s;
          display: flex;
          align-items: center;
          -webkit-tap-highlight-color: transparent;
          min-width: 44px;
          min-height: 44px;
          justify-content: center;
        }

        .eye-btn:hover { color: var(--navy); }

        .or-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 0.75rem;
          margin-bottom: clamp(1rem, 2.5vw, 1.5rem);
        }

        .rem-lbl {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          cursor: pointer;
          font-family: 'DM Mono', monospace;
          font-size: 9px;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: #051173;
          user-select: none;
        }

        .rem-chk {
          width: 14px;
          height: 14px;
          border: 1.5px solid #051173;
          appearance: none;
          -webkit-appearance: none;
          cursor: pointer;
          position: relative;
          transition: border-color 0.2s, background 0.2s;
          flex-shrink: 0;
          border-radius: 2px;
        }

        .rem-chk:checked { background: var(--navy); border-color: var(--navy); }

        .rem-chk:checked::after {
          content: '';
          position: absolute;
          top: 1px;
          left: 3px;
          width: 5px;
          height: 8px;
          border-right: 1.5px solid white;
          border-bottom: 1.5px solid white;
          transform: rotate(40deg);
        }

        .fgt {
          font-family: 'DM Mono', monospace;
          font-size: 9px;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: #051173;
          text-decoration: none;
          border-bottom: 1px solid transparent;
          transition: color 0.2s, border-color 0.2s;
        }

        .fgt:hover { color: var(--gold); border-color: var(--gold); }

        /* ── Sign In + Google: equal width, same line ── */
        .btn-row {
          display: flex;
          flex-direction: row;
          gap: 0.75rem;
          align-items: stretch;
          margin-bottom: 1rem;
        }

        .btn-row .sbtn {
          flex: 1;
          margin-bottom: 0;
          min-height: 48px;
        }

        .btn-row .soc-google {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.45rem;
          padding: 0.65rem;
          border: 1.5px solid #e0e0e0;
          background: var(--white);
          border-radius: 100px;
          cursor: pointer;
          font-family: 'DM Mono', monospace;
          font-size: 10px;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          color: var(--black);
          transition: background 0.25s, border-color 0.25s, color 0.25s, box-shadow 0.25s, transform 0.15s;
          -webkit-tap-highlight-color: transparent;
          touch-action: manipulation;
          min-height: 48px;
        }

        .btn-row .soc-google:hover {
          background: var(--navy);
          border-color: var(--navy);
          color: var(--white);
          box-shadow: 0 4px 16px rgba(5,17,115,0.18);
          transform: translateY(-1px);
        }

        .btn-row .soc-google:hover svg path { fill: white !important; }
        .btn-row .soc-google:active { transform: translateY(0); }
        .btn-row .soc-google svg { width: 15px; height: 15px; flex-shrink: 0; }

        .sbtn {
          width: 100%;
          background: #051173;
          color: var(--white);
          border: 2px solid var(--navy);
          padding: 0.9rem 2rem;
          font-family: 'DM Mono', monospace;
          font-size: 10px;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          cursor: pointer;
          border-radius: 100px;
          transition: background 0.3s, border-color 0.3s, transform 0.15s, box-shadow 0.3s;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.6rem;
          margin-bottom: 1rem;
          -webkit-tap-highlight-color: transparent;
          touch-action: manipulation;
          min-height: 48px;
        }

        .sbtn:hover:not(:disabled) { background: var(--gold); border-color: var(--gold); transform: translateY(-2px); box-shadow: 0 8px 24px rgba(199,171,95,0.4); }
        .sbtn:active:not(:disabled) { transform: translateY(0); }
        .sbtn:disabled { opacity: 0.65; cursor: not-allowed; }

        .spin {
          width: 13px;
          height: 13px;
          border: 2px solid rgba(255,255,255,0.35);
          border-top-color: white;
          border-radius: 50%;
          animation: sp 0.8s linear infinite;
        }

        @keyframes sp { to { transform: rotate(360deg); } }

        .cta-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.55rem;
          width: 100%;
          background: transparent;
          color: var(--navy);
          border: 1.5px solid rgba(5,17,115,0.3);
          padding: 0.9rem 1.5rem;
          font-family: 'DM Mono', monospace;
          font-size: 10px;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          cursor: pointer;
          border-radius: 100px;
          text-decoration: none;
          transition: background 0.3s, border-color 0.3s, color 0.3s, transform 0.2s, box-shadow 0.3s;
          min-height: 48px;
          -webkit-tap-highlight-color: transparent;
          touch-action: manipulation;
          margin-bottom: 1rem;
        }

        .cta-btn:hover {
          background: #051173;
          border-color: #051173;
          color: white;
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(5,17,115,0.2);
        }

        .ord {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin-bottom: 1rem;
        }

        .ord::before, .ord::after { content: ''; flex: 1; height: 1px; background: #e0e0e0; }
        .ort { font-family: 'DM Mono', monospace; font-size: 9px; letter-spacing: 0.2em; text-transform: uppercase; color: #999; white-space: nowrap; }

        .srow {
          display: flex;
          flex-direction: row;
          flex-wrap: nowrap;
          gap: 0.65rem;
          margin-bottom: clamp(1rem, 2.5vw, 1.5rem);
        }

        .soc {
          flex: 1 1 0;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.45rem;
          padding: 0.65rem;
          border: 1.5px solid #e0e0e0;
          background: var(--white);
          border-radius: 8px;
          cursor: pointer;
          font-family: 'DM Mono', monospace;
          font-size: 9px;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: var(--black);
          transition: background 0.25s, border-color 0.25s, color 0.25s, box-shadow 0.25s, transform 0.15s;
          -webkit-tap-highlight-color: transparent;
          touch-action: manipulation;
          min-height: 44px;
        }

        .soc:hover { background: var(--navy); border-color: var(--navy); color: var(--white); box-shadow: 0 4px 16px rgba(5,17,115,0.18); transform: translateY(-1px); }
        .soc:hover svg path { fill: white !important; }
        .soc:active { transform: translateY(0); }
        .soc svg { width: 15px; height: 15px; flex-shrink: 0; transition: none; }

        @media (max-width: 768px) {
          .bg-brand, .bg-stats, .bg-compass { display: none; }
          .bg-badge { display: none; }
          .c-tl, .c-tr, .c-bl, .c-br { display: none; }
          .form-wrap {
            align-items: flex-start;
            padding: 1.5rem 0.75rem 1.5rem;
            min-height: 100dvh;
          }
          .fc {
            border-radius: 12px;
            margin: 0 auto;
          }
        }

        @media (max-width: 380px) {
          .srow { flex-direction: column; }
          .soc { width: 100%; }
          .btn-row { flex-direction: column; }
          .btn-row .sbtn,
          .btn-row .soc-google { width: 100%; flex: unset; }
        }

        @media (max-height: 600px) {
          .c-tl, .c-tr, .c-bl, .c-br { display: none; }
          .bg-badge { display: none; }
          .form-wrap { padding: 1rem 0.5rem; }
          .fc { border-radius: 10px; }
        }
      `}</style>

      <div
        className="lr"
        onMouseMove={(e) => {
          if (window.innerWidth > 768) spawnRipple(e.clientX, e.clientY);
        }}
        onClick={(e) => spawnRipple(e.clientX, e.clientY)}
      >
        <video className="bg-video" src="/home.mp4" autoPlay loop muted playsInline />
        <div className="bg-scrim" />
        <div className="bg-tex" />
        <canvas ref={canvasRef} className="bg-canvas" />

        <div className="c-tl" />
        <div className="c-tr" />
        <div className="c-bl" />
        <div className="c-br" />

        <div className="bg-compass">
          <svg width="80" height="80" viewBox="0 0 72 72" fill="none">
            <circle cx="36" cy="36" r="34" stroke="#C7AB5F" strokeWidth="0.7" />
            <circle cx="36" cy="36" r="24" stroke="#C7AB5F" strokeWidth="0.45" />
            <circle cx="36" cy="36" r="4" stroke="#C7AB5F" strokeWidth="0.7" />
            <line x1="36" y1="2" x2="36" y2="70" stroke="#C7AB5F" strokeWidth="0.45" />
            <line x1="2" y1="36" x2="70" y2="36" stroke="#C7AB5F" strokeWidth="0.45" />
            <polygon points="36,4 38,32 36,36 34,32" fill="#C7AB5F" opacity="0.75" />
          </svg>
        </div>

        <div className="bg-stats">
          {[["10+", "Years"], ["1k+", "Clients"], ["4.9★", "Rated"]].map(([n, l]) => (
            <div className="bg-stat" key={l}>
              <div className="bg-stat-n">{n}</div>
              <div className="bg-stat-l">{l}</div>
            </div>
          ))}
        </div>

        <div className="bg-badge">Secure Login</div>

        <div className="form-wrap">
          <div className={`fc ${mounted ? "on" : ""}`}>

            <div className="fc-header">
              <div className="gold-bar" />
              <button
                className="close-btn"
                type="button"
                onClick={handleClose}
                aria-label="Go to home page"
              >
                <X size={14} />
              </button>
            </div>

            <p className="f-eye">Member Access</p>
            <h1 className="f-title">Welcome Back</h1>
            <p className="f-sub">Sign in to your account</p>

            {error && (
              <div className="alert-error">
                <span className="alert-icon">✕</span>
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="fg">
                <label className="fl" htmlFor="email">Email Address</label>
                <div className="fiw">
                  <input
                    id="email"
                    type="email"
                    required
                    placeholder="your@email.com"
                    className={`fi ${formData.email ? "hv" : ""}`}
                    value={formData.email}
                    onChange={(e) => {
                      setFormData({ ...formData, email: e.target.value });
                      setError("");
                    }}
                  />
                  <div className="fu" />
                </div>
              </div>

              <div className="fg">
                <label className="fl" htmlFor="password">Password</label>
                <div className="fiw">
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    required
                    placeholder="Your password"
                    className={`fi ${formData.password ? "hv" : ""}`}
                    value={formData.password}
                    onChange={(e) => {
                      setFormData({ ...formData, password: e.target.value });
                      setError("");
                    }}
                  />
                  <div className="fu" />
                  <button
                    type="button"
                    className="eye-btn"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
              </div>

              <div className="or-row">
                <label className="rem-lbl">
                  <input
                    type="checkbox"
                    className="rem-chk"
                    checked={formData.remember}
                    onChange={(e) =>
                      setFormData({ ...formData, remember: e.target.checked })
                    }
                  />
                  Remember me
                </label>
                <a href="/forgot-password" className="fgt">Forgot password?</a>
              </div>

              {/* ── Sign In + Google: equal width on same line ── */}
              <div className="btn-row">
                <button type="submit" className="sbtn" disabled={isLoading}>
                  {isLoading ? (
                    <>
                      <div className="spin" />
                      Signing In
                    </>
                  ) : (
                    "Sign In"
                  )}
                </button>

                <button
                  type="button"
                  className="soc-google"
                  onClick={handleGoogleLogin}
                  aria-label="Sign in with Google"
                >
                  <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                  Google
                </button>
              </div>
            </form>

            <a href="/registration" className="cta-btn">
              Create Your Account
            </a>

          </div>
        </div>
      </div>
    </>
  );
}